import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { constants } from 'app/constants/constants';
import { GlobalService } from 'app/global/global.service';
import { AddRevenueSidebarComponent } from 'app/main/pages/contract-control/contract-control-detail-view/cc-revenue/add-revenue-sidebar/add-revenue-sidebar.component';
import { IRevenueForm } from 'app/main/pages/contract-control/contract-control-detail-view/cc-revenue/revenue.dto';
import { ActionEnum } from 'app/main/pages/contract-control/modals/add-cost-modal/add-cost-modal.component';
import { AddToContractControlFormHelper } from '../add-to-contract-control-modal.component';
import { IAutoFillUploadFile } from '../create-cc-by-ai-form/ai-cc-import.service';

@Component({
  selector: 'app-revenue-form',
  templateUrl: './revenue-form.component.html',
  styleUrl: './revenue-form.component.scss'
})
export class RevenueFormComponent implements OnInit{
  
  @Input() contractForm: FormGroup;
  @Input() aiProcessedData: IAutoFillUploadFile;
  
  public systemCurrency: string = '';

  constructor (
    private _addToContractControlFormHelper: AddToContractControlFormHelper,
    private _formBuilder: FormBuilder,
    private _modalService: NgbModal,
    private _globalService: GlobalService
  ){
    this.systemCurrency = this._globalService.getSystemCurrency();
  }

  ngOnInit(): void {
    if(this.aiProcessedData) {
        this.initializeWithAIProcessedData(this.aiProcessedData);
    }
  }
  
    /**
     * Initializes the revenue form with data extracted by AI.
     * 
     * @param aiProcessedData Data extracted from a document by AI.
     */
    initializeWithAIProcessedData(aiProcessedData: IAutoFillUploadFile): void {
      // Prevent duplicating AI seeded revenue when user revisits this step.
      if (this.revenueControls.dirty || this.revenueControls.length > 0) {
        return;
      }

      if (!aiProcessedData?.revenue) return;

      const defaultForm = this._addToContractControlFormHelper.getRevenueForm().value;
      const revenue_amount = isNaN(Number(aiProcessedData?.revenue?.revenue_amount)) ? defaultForm.revenue_amount : Number(aiProcessedData?.revenue?.revenue_amount);
      const currency = constants.contract_control_currencies.some(x => x?.value?.toLocaleLowerCase().trim() === aiProcessedData?.revenue?.revenue_currency?.toLocaleLowerCase().trim()) 
        ? aiProcessedData?.revenue?.revenue_currency?.trim() 
        : defaultForm.revenue_currency;

      const newRevenue: Partial<IRevenueForm> = {
          description: 'AI Processed Revenue',
          revenue_amount: revenue_amount,
          revenue_currency: currency,
          billing_frequency: 'monthly', // Default for AI entries if not provided
          billing_start: aiProcessedData?.period?.start_date || new Date().toISOString().split('T')[0],
          has_billing_end_date: !!aiProcessedData?.period?.end_date,
          billing_end: aiProcessedData?.period?.end_date || null
      };

      this.revenueControls.push(this._formBuilder.group(newRevenue));
    }
  



    /**
     * Opens the revenue form modal for adding or editing a revenue entry.
     * 
     * @param revenue Optional. The revenue form data to prefill the modal with.
     * @param index Optional. The index of the revenue in the array for editing.
     */
    openRevenueFormModal(revenue?: IRevenueForm, index?: number): void {
      const modalRef$ = this._modalService.open(AddRevenueSidebarComponent, { backdrop: 'static', size: 'md' });
      modalRef$.componentInstance.revenue = revenue;
      modalRef$.componentInstance.document_type_id = this.contractForm.get('contractForm')?.get('document_type_id')?.value;
      modalRef$.componentInstance.contract_control_id = null;
      modalRef$.componentInstance.hasPermission = true;
      modalRef$.componentInstance.actionFrom = ActionEnum.CREATING_CONTRACT;
      modalRef$.result.then((result: IRevenueForm) => {
          if(result) {
              if (index !== undefined) {
                  this.revenueControls.at(index).patchValue(result);
              } else {
                  this.revenueControls.push(this._formBuilder.group(result));
              }
              this.revenueControls.markAsDirty();
          }
      });
    }

    /**
     * Removes a revenue from the FormArray.
     * @param index The index of the revenue to remove.
     */
    removeRevenue(index: number): void {
      this.revenueControls.removeAt(index);
      this.revenueControls.markAsDirty();
    }


    get revenueControls() {
        return this.contractForm.get('revenueForm') as FormArray;
    }
}
